import { useMemo, useState, type Dispatch, type SetStateAction } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import {
  capabilityOf, friendlyGroup, idsForLevel, levelOf, METHOD_STYLE,
  type Capability, type Level, type PermView,
} from './rolesUtils'
import { Box, CAP_LABEL, CAP_ON, LEVEL_ON, LevelPills, SearchBox } from './rolesUi'

type Filter = 'all' | 'selected' | 'unselected'
const GLANCE_LIMIT = 10   // area tags shown per level before "+N more"

/**
 * The permission picker for one role: a quick-set row, an "access at a glance" line, then one row per
 * area with a single None / View / Edit / Full choice (open an area to tick individual permissions).
 */
export function PermissionEditor({ views, draft, setDraft }: {
  views: PermView[]
  draft: Set<number>
  setDraft: Dispatch<SetStateAction<Set<number>>>
}) {
  const [permSearch, setPermSearch] = useState('')
  const [filter, setFilter] = useState<Filter>('all')
  const [open, setOpen] = useState<Record<string, boolean>>({})
  const [showAllChips, setShowAllChips] = useState<Record<string, boolean>>({})

  const searching = permSearch.trim().length > 0

  // Every area with all of its permissions (unfiltered) — levels always act on the whole area.
  const allGroups = useMemo(() => {
    const m = new Map<string, PermView[]>()
    for (const v of views) { if (!m.has(v.group)) m.set(v.group, []); m.get(v.group)!.push(v) }
    return m
  }, [views])

  // What is shown after search + filter.
  const groups = useMemo(() => {
    const q = permSearch.trim().toLowerCase()
    const map = new Map<string, PermView[]>()
    for (const v of views) {
      if (q && !(`${v.label} ${v.raw} ${v.group}`.toLowerCase().includes(q))) continue
      const isOn = draft.has(v.id)
      if (filter === 'selected' && !isOn) continue
      if (filter === 'unselected' && isOn) continue
      if (!map.has(v.group)) map.set(v.group, [])
      map.get(v.group)!.push(v)
    }
    return [...map.entries()].sort(([a], [b]) => friendlyGroup(a).localeCompare(friendlyGroup(b)))
  }, [views, permSearch, filter, draft])

  // Totals per area over the whole catalog (not just the filtered rows).
  const groupTotals = useMemo(() => {
    const t = new Map<string, { total: number; on: number }>()
    for (const v of views) {
      const g = t.get(v.group) ?? { total: 0, on: 0 }
      g.total++; if (draft.has(v.id)) g.on++
      t.set(v.group, g)
    }
    return t
  }, [views, draft])

  const setLevel = (items: PermView[], level: Level) =>
    setDraft(s => {
      const n = new Set(s)
      items.forEach(i => n.delete(i.id))
      idsForLevel(items, level).forEach(id => n.add(id))
      return n
    })

  // "At a glance": every area the role touches, with its level.
  const glance = useMemo(() => {
    const out: { group: string; level: Level | 'custom' }[] = []
    allGroups.forEach((items, g) => {
      const level = levelOf(items, draft)
      if (level !== 'none') out.push({ group: g, level })
    })
    return out.sort((a, b) => friendlyGroup(a.group).localeCompare(friendlyGroup(b.group)))
  }, [allGroups, draft])

  // Which kinds of action are switched on in an area: view / add-or-change / delete.
  const capsOf = (items: PermView[]) => {
    const on = new Set<Capability>()
    items.forEach(i => { if (draft.has(i.id)) on.add(capabilityOf(i)) })
    return on
  }

  const toggle = (id: number) => setDraft(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })
  const setMany = (ids: number[], on: boolean) => setDraft(s => { const n = new Set(s); ids.forEach(id => on ? n.add(id) : n.delete(id)); return n })
  const isOpen = (g: string) => searching || (open[g] ?? false)
  const visibleIds = groups.flatMap(([, items]) => items.map(i => i.id))

  // The summary: areas grouped by their level, strongest first.
  const summaryRows = useMemo(() => {
    const defs: { level: Level | 'custom'; title: string; hint: string }[] = [
      { level: 'full',   title: 'Full control', hint: 'view, add, change, delete' },
      { level: 'edit',   title: 'Can edit',     hint: 'view, add, change' },
      { level: 'view',   title: 'View only',    hint: 'look, no changes' },
      { level: 'custom', title: 'Custom',       hint: 'hand-picked mix' },
    ]
    return defs
      .map(d => ({ ...d, areas: glance.filter(g => g.level === d.level).map(g => g.group) }))
      .filter(d => d.areas.length > 0)
  }, [glance])

  return (
    <>
      {/* access summary: one row per level, areas listed as small tags */}
      <div className="mb-4 rounded-xl border border-gray-200 bg-white">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
          <h4 className="text-sm font-semibold text-gray-900">This role can access</h4>
          <span className="text-xs text-gray-500">{glance.length} of {allGroups.size} areas</span>
        </div>
        {glance.length === 0 ? (
          <p className="px-4 py-4 text-sm text-gray-500">No access yet. Pick a level (None / View / Edit / Full) for the areas below.</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {summaryRows.map(row => {
              const expanded = showAllChips[row.level]
              const tags = expanded ? row.areas : row.areas.slice(0, GLANCE_LIMIT)
              return (
                <div key={row.level} className="flex gap-3 px-4 py-3">
                  <div className="w-32 shrink-0">
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold ${row.level === 'custom' ? 'bg-purple-100 text-purple-700' : LEVEL_ON[row.level]}`}>
                      {row.title}<span className="opacity-80">{row.areas.length}</span>
                    </span>
                    <p className="mt-1 text-[11px] leading-tight text-gray-400">{row.hint}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 min-w-0">
                    {tags.map(a => (
                      <button key={a} onClick={() => setPermSearch(friendlyGroup(a))} title={`Show ${friendlyGroup(a)} below`}
                        className="rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-700 hover:bg-brand-50 hover:text-brand-700 transition-colors">
                        {friendlyGroup(a)}
                      </button>
                    ))}
                    {row.areas.length > GLANCE_LIMIT && (
                      <button onClick={() => setShowAllChips(s => ({ ...s, [row.level]: !expanded }))}
                        className="text-xs font-medium text-brand-600 hover:underline px-1">
                        {expanded ? 'Show fewer' : `+${row.areas.length - GLANCE_LIMIT} more`}
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <SearchBox value={permSearch} onChange={setPermSearch} placeholder="Search permissions (e.g. booking, delete, payout)…" className="flex-1 min-w-[220px]" />
        <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs font-medium">
          {(['all', 'selected', 'unselected'] as Filter[]).map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-2 capitalize ${filter === f ? 'bg-brand-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>{f === 'unselected' ? 'Not selected' : f}</button>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3 text-xs text-gray-500">
        <span><b className="text-gray-800">{draft.size}</b> of {views.length} permissions selected</span>
        <span className="flex items-center gap-3">
          <button className="hover:text-brand-600 disabled:opacity-40" disabled={!visibleIds.length} onClick={() => setMany(visibleIds, true)}>Select all{searching || filter !== 'all' ? ' shown' : ''}</button>
          <button className="hover:text-brand-600 disabled:opacity-40" disabled={!visibleIds.length} onClick={() => setMany(visibleIds, false)}>Clear{searching || filter !== 'all' ? ' shown' : ''}</button>
          {!searching && <>
            <button className="hover:text-brand-600" onClick={() => setOpen(Object.fromEntries(groups.map(([g]) => [g, true])))}>Expand all</button>
            <button className="hover:text-brand-600" onClick={() => setOpen({})}>Collapse all</button>
          </>}
        </span>
      </div>

      {/* areas */}
      {groups.length === 0 ? (
        <p className="text-sm text-gray-400 py-10 text-center">{views.length === 0 ? 'No permissions defined.' : 'No permissions match your search or filter.'}</p>
      ) : (
        <div className="space-y-2">
          {groups.map(([g, items]) => {
            const t = groupTotals.get(g) ?? { total: items.length, on: 0 }
            const whole = allGroups.get(g) ?? items
            const level = levelOf(whole, draft)
            const caps = capsOf(whole)
            return (
              <div key={g} className="border border-gray-100 rounded-xl overflow-hidden">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2.5 bg-gray-50">
                  <button className="flex-1 min-w-[140px] flex items-center gap-2 text-left" onClick={() => setOpen(o => ({ ...o, [g]: !isOpen(g) }))}
                    title="Show the individual permissions">
                    {isOpen(g) ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
                    <span className="text-sm font-semibold text-gray-800">{friendlyGroup(g)}</span>
                    <span className={`text-xs ${t.on ? 'text-brand-600 font-medium' : 'text-gray-400'}`}>{t.on}/{t.total}</span>
                    <span className="hidden sm:flex items-center gap-1">
                      {(['view', 'edit', 'delete'] as Capability[]).map(c => (
                        <span key={c} className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${caps.has(c) ? CAP_ON[c] : 'bg-gray-100 text-gray-300'}`}>{CAP_LABEL[c]}</span>
                      ))}
                    </span>
                    {level === 'custom' && <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-purple-50 text-purple-700">Custom</span>}
                  </button>
                  <LevelPills active={level} onPick={l => setLevel(whole, l)} />
                </div>
                {isOpen(g) && (
                  <div className="grid sm:grid-cols-2 gap-1.5 p-2.5">
                    {items.map(v => {
                      const on = draft.has(v.id)
                      return (
                        <button key={v.id} title={v.raw} onClick={() => toggle(v.id)}
                          className={`flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm border text-left transition-colors ${on ? 'border-brand-300 bg-brand-50' : 'border-gray-100 hover:bg-gray-50'}`}>
                          <Box state={on ? 'all' : 'none'} small />
                          {v.method && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${METHOD_STYLE[v.method] ?? 'bg-gray-100 text-gray-600'}`}>{v.method}</span>}
                          <span className={`truncate ${on ? 'text-brand-800' : 'text-gray-700'}`}>{v.label}</span>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </>
  )
}
